import { Component, ViewChild, ElementRef, ViewEncapsulation, Input, NgZone, ChangeDetectorRef } from '@angular/core';
import { LookupList, LookupListItem } from "@common/data-models/complex-types/lookup-list";
import { EnpsControl } from '../enps-control/enps-control';
import { debounceTime, exhaustMap, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { from, Observable } from 'rxjs';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatSelectionListChange } from '@angular/material/list';
import { NumberInput, coerceNumberProperty } from '@angular/cdk/coercion';
import { AppService } from '@suite/app.service';
import { TimeoutQueue } from '@common/services/timeout-queue';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { NavigationService } from '@common/services/navigation-service';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { OPTION_BLANK } from '../../enums/constants';
import { LanguageSettings } from '@common/services/language-settings';

@Component({
    selector: 'tags-autocomplete',
    templateUrl: './tags-autocomplete.html',
    // changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./tags-autocomplete.scss'],
    host: {
        class: 'st-tags-autocomplete'
    }
})

export class TagsAutocomplete extends EnpsControl<LookupList> {
    tagsFilter = new FormControl('');

    @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

    private _maxItemsToShow = 999;
    @Input()
    get maxItemsToShow(): number {
        return this._maxItemsToShow;
    }
    set maxItemsToShow(value: NumberInput) {
        this._maxItemsToShow = coerceNumberProperty(value);
        this.calculateTagsToShow();
    }

    @Input() public includeBlank: boolean = false;

    public get items(): LookupListItem[] {
        if (this.tagsToShow?.length > 0) {
            return this.tagsToShow;
        }
        return this.value;
    }

    public tagsToShow: LookupListItem[] = [];
    public tagsAsTooltip: string = '';
    public set value(v: LookupList) {
        super.value = v;
        this.calculateTagsToShow();
    }

    public get value(): LookupList {
        return super.value;
    }

    constructor(
        public languageSettings: LanguageSettings,
        public appService: AppService,
        protected ngZone: NgZone,
        protected changeDetectorRef: ChangeDetectorRef,
        timeoutQueue: TimeoutQueue,
        public globalTablesService: GlobalTablesService,
        public navigationService: NavigationService,
        protected elmRef: ElementRef
    ) {
        super(appService, ngZone, changeDetectorRef, timeoutQueue, globalTablesService, elmRef);
    }

    private calculateTagsToShow() {
        // if (this.asCustomField) {
        this.tagsToShow = this._value?.slice(0, this.maxItemsToShow);
        this.tagsAsTooltip = this._value?.slice(this.maxItemsToShow)
            .map(item => item.displayText).join('\n');
        // }
    }

    suggestions: Observable<LookupListItem[]>;

    valueChanged() {
        if (this.isValueChanged) {
            this.isValueChanged = false;
            this.changed.emit(this.value);
        }
    }

    onMenuOpened() {
        super.onMenuOpened();
        this.suggestions = this.tagsFilter.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            takeUntil(this.destroyed$),
            switchMap(value => this.getSuggestions(value || '')),
        );
    }

    public getSuggestions(val: string): Observable<LookupListItem[]> {
        //if (val.trim().length > 0) {
        return from(this.appService.nomWebServicesJson.getSuggestions(val)).pipe(
            map(res => {
                if(this.includeBlank) {
                    return [new LookupListItem(OPTION_BLANK, this.languageSettings.PSTWebBlankOption, this.languageSettings.PSTWebBlankOption), ...res.data.map(item => new LookupListItem(item, item, item))];
                } else {
                    return res.data.map(item => new LookupListItem(item, item, item));
                }
            })
        );
        //}
        // return from([]);
    }

    add(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        const lookupListItem = new LookupListItem(value, value, value);

        if (value && !this.valueContains(lookupListItem)) {
            this.value.push(lookupListItem);
            event.chipInput!.clear();
            this.tagsFilter.setValue('');
            this.calculateTagsToShow();
            this.isValueChanged = true;
        }
        this.menuTrigger.closeMenu();
    }

    selected(event: MatSelectionListChange): void {
        const item = event.options[0]?.value;
        const lookupListItem = new LookupListItem(item.id, item.displayText, item.description);
        if (item && item.id && !this.valueContains(lookupListItem)) {
            this.value.push(lookupListItem);
            this.searchBox.nativeElement.value = '';
            this.tagsFilter.setValue('');
            this.calculateTagsToShow();
            this.isValueChanged = true;
        }
        this.menuTrigger.closeMenu();
    }

    remove(tag: LookupListItem): void {
        const index = this.value.findIndex(item => item.id === tag.id);

        if (index >= 0) {
            this.value.splice(index, 1);
        }
        this.isValueChanged = true;
        this.calculateTagsToShow();
        this.valueChanged();
    }

    search(tag: LookupListItem) {
        if (tag && tag.id) {
            let searchTerm: string = "#" + tag.id.trim();
            if (this.appService.appSupportMap[this.navigationService.Application].supportsSearchMode) {
                this.navigationService.enterSearchMode(searchTerm);
            } else {
                this.navigationService.enterSearchMode(searchTerm, false, this.appService.appSupportMap[this.navigationService.Application].redirectTo);
            }
        }
    }

    valueContains(tag: LookupListItem): boolean {
        return this.value?.findIndex(item => item.id === tag.id) >= 0;
    }

}
